import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const KEY = "aurum_lang";

const dict = {
  fr: {
    // ===== COMMON =====
    "common.loading": "Chargement…",
    "common.loading_dashboard": "Chargement de la formation…",
    "common.access": "Accéder",
    "common.access_full": "Accéder à la formation",
    "common.see_program": "Voir le programme",
    "common.discover": "Découvrir",
    "common.download": "Télécharger",
    "common.download_pdf": "Télécharger PDF",
    "common.contact": "Contact",
    "common.back_home": "Retour à l'accueil",
    "common.year_copyright": "© 2026 — Tous droits réservés",

    // ===== LANG TOGGLE =====
    "lang.fr": "FR",
    "lang.en": "EN",
    "lang.toggle_aria": "Changer de langue",

    // ===== LANDING — HEADER =====
    "header.nav.promise": "Promesse",
    "header.nav.sectors": "Secteurs",
    "header.nav.training": "Formation",
    "header.nav.strategy": "Stratégie",
    "header.nav.resources": "Ressources",

    // ===== LANDING — HERO =====
    "hero.eyebrow": "Devenez apporteur d'affaires international",
    "hero.title.from": "Passez de",
    "hero.title.to": "à",
    "hero.title.permonth": "par mois",
    "hero.title.signing": "en signant des",
    "hero.title.contracts": "contrats",
    "hero.subtitle": "La méthode pas-à-pas pour sourcer, qualifier, cadrer et sécuriser des transactions à forte valeur ajoutée sur l'or, les pierres précieuses, le pétrole, les métaux, l'horlogerie, le ciment et les commodités alimentaires — et toucher vos commissions sans vous faire bypasser.",
    "hero.badge.method": "Méthode vérifiée sur le terrain",
    "hero.badge.records": "Centaines de credit records",
    "hero.badge.deals": "Deals successful documentés",
    "hero.stat.sectors": "Secteurs couverts",
    "hero.stat.modules": "Modules",
    "hero.stat.suppliers": "Fournisseurs",
    "hero.note": "Accès nominatif par code unique. Contenu non redistribuable. Protection de votre rôle d'intermédiaire par",

    // ===== LANDING — MARQUEE =====
    "marquee.gold": "Or brut & Doré",
    "marquee.diamonds": "Diamants GIA",
    "marquee.watches": "Horlogerie B2B",
    "marquee.oil": "EN590 · JP54 · Mazut",
    "marquee.metals": "Cuivre · Aluminium · Nickel",
    "marquee.soft": "Sucre · Café · Cacao",
    "marquee.docs": "NCNDA · LOI · ICPO · IMFPA",

    // ===== LANDING — PROMISE =====
    "promise.eyebrow": "La promesse",
    "promise.title1": "Une méthode",
    "promise.title2": "vérifiée",
    "promise.title3": "Une crédibilité à",
    "promise.intro": "Pas de théorie creuse. Cette formation est construite à partir de centaines de credit records, de deals réels signés, et des erreurs concrètes que 99 % des intermédiaires commettent. Vous récupérez le système — pas l'histoire.",
    "promise.pillar1.title": "Trouver les bons deals",
    "promise.pillar1.body": "Sourcer des acheteurs solvables et des vendeurs qui livrent réellement. Décoder un SCO, ICPO ou FCO en 2 minutes. Filtrer 95 % des amateurs avant même de répondre.",
    "promise.pillar2.title": "Cadrer & sécuriser",
    "promise.pillar2.body": "NCNDA, LOI, IMFPA, SBLC, MT760 — la séquence documentaire qui blinde votre commission. Plus jamais de bypass, plus jamais de chaîne fantôme.",
    "promise.pillar3.title": "Encaisser sans bruit",
    "promise.pillar3.body": "Banque, juridiction, structure de rémunération. Toucher 5, 10, 20 k$ par mois proprement, en optimisant votre fiscalité dès le 1er deal.",
    "promise.kpi1.label": "Objectif mensuel",
    "promise.kpi2.label": "Or, pétrole, métaux, soft…",
    "promise.kpi2.value": "7 secteurs",
    "promise.kpi3.label": "Du sourcing à la fiscalité",
    "promise.kpi3.value": "11 modules",
    "promise.kpi4.label": "Fournisseurs vérifiés",
    "promise.kpi4.value": "40+ contacts",

    // ===== LANDING — SECTORS =====
    "sectors.eyebrow": "Secteurs couverts",
    "sectors.title1": "Sept marchés.",
    "sectors.title2": "Une seule méthode.",
    "sectors.intro": "Chaque secteur a sa liturgie documentaire, ses bourses de référence, ses pièges et ses primes régionales. AURUM décrypte chacun — du sourcing à la signature. Vous démarrez sur le secteur que vous voulez, et vous ouvrez les autres comme bon vous semble.",
    "sectors.gold.title": "Or",
    "sectors.gold.sub": "Doré, lingots, raffinage LBMA / Dubai",
    "sectors.oil.title": "Pétrole",
    "sectors.oil.sub": "EN590 · JP54 · Mazut · D2 · Crude",
    "sectors.gems.title": "Pierres précieuses",
    "sectors.gems.sub": "Diamants GIA · émeraudes · rubis · saphirs",
    "sectors.watches.title": "Horlogerie",
    "sectors.watches.sub": "Rolex · AP · Patek · Richard Mille — B2B",
    "sectors.cement.title": "Ciment",
    "sectors.cement.sub": "Clinker · CEM I-V · vrac & big bag",
    "sectors.metals.title": "Métaux",
    "sectors.metals.sub": "Cu · Al · Zn · Pb · Ni · Sn · Ag · Co · Mg",
    "sectors.soft.title": "Commodités & Alimentaire",
    "sectors.soft.sub": "Sucre · Café · Cacao · Blé · Riz · Palme",

    // ===== LANDING — FORMATION =====
    "formation.eyebrow": "Modules de formation",
    "formation.title1": "Le cœur du métier.",
    "formation.title2": "Sept piliers",
    "formation.title3": "indispensables",
    "formation.intro": "De la négociation à la fiscalité, en passant par la sécurisation des deals et le banking international — vous obtenez le système complet pour transformer une opportunité en commission réellement encaissée.",
    "formation.m1.title": "Négociation internationale",
    "formation.m1.body": "Méthode Harvard appliquée à l'intermédiation. Les 5 étapes d'une négociation gagnante, les commissions par secteur, gérer les silences, repérer les bluffs, naviguer les 4 zones culturelles et lire les 7 signaux de sortie.",
    "formation.m2.title": "Banking",
    "formation.m2.body": "8 juridictions stratégiques avec leurs taux d'imposition, comment ouvrir un compte aux UAE et à Maurice, le réseau SWIFT et les banques correspondantes, les frais de virement comparés, offshore vs étranger.",
    "formation.m3.title": "Sécurisation & Red Flags",
    "formation.m3.body": "Les 5 arnaques classiques en intermédiation, vérifier une LC ou SBLC en 6 étapes, la règle des 3T, le KYC en 5 étapes avec les 8 outils à utiliser.",
    "formation.m4.title": "Pricing & Valorisation",
    "formation.m4.body": "Sources officielles par commodité, lire une cotation Bloomberg, comprendre primes et décotes, calculer le prix d'1 tonne d'or, comparatif FOB vs CIF.",
    "formation.m5.title": "Fiscalité & Rémunération",
    "formation.m5.body": "Les 3 modes de rémunération en intermédiation, 6 entités fiscales (SASU, LLC UAE, DMCC, GBL Maurice…), conventions bilatérales, arbitrage selon votre profil et votre volume.",
    "formation.m6.title": "Outils pratiques",
    "formation.m6.body": "4 modèles d'email avec bouton copier, 4 CRM recommandés, 5 plateformes d'e-signature, les 10 règles d'or de l'intermédiaire, 20 bookmarks cliquables par catégorie.",
    "formation.m7.title": "Mindset du Deal Maker",
    "formation.m7.body": "Posture, identité, gestion du long cycle de vente. Comment penser comme un broker respecté plutôt qu'un courtier de passage. Le travail intérieur que personne n'enseigne.",

    // ===== LANDING — STRATEGY =====
    "strategy.eyebrow": "Modules stratégiques",
    "strategy.title1": "La technique ne suffit pas.",
    "strategy.title2": "Le",
    "strategy.title3": "positionnement",
    "strategy.title4": "décide.",
    "strategy.intro": "Quatre modules transversaux qui font la différence entre un intermédiaire de passage et un broker respecté. C'est ce qui transforme la maîtrise technique en revenus mensuels stables.",
    "strategy.s1.title": "Deal Intelligence & Filtering",
    "strategy.s1.body": "Repérer un faux seller en 2 minutes, décoder les red flags dans un SCO / ICPO / FCO, neutraliser les 4 structures de scam classiques (daisy chain, fake mandate, advance fee, test shipment piégé).",
    "strategy.s2.title": "Pouvoir & Positionnement",
    "strategy.s2.body": "Devenir mandataire plutôt que middleman. Se rendre indispensable dans une chaîne. Sécuriser sa commission via NCNDA et IMFPA. Parler le langage des HNWI, traders et fournisseurs.",
    "strategy.s3.title": "Accès & Réseau",
    "strategy.s3.body": "Approcher un fournisseur sans se faire ignorer. Scripts prêts à l'emploi pour Email / LinkedIn / WhatsApp. Construire un réseau crédible de 20 contacts utiles en 30 jours, semaine par semaine.",
    "strategy.s4.title": "Mindset transversal",
    "strategy.s4.body": "L'identité du deal maker : posture, gestion du long cycle, résilience. La différence mentale entre un broker à 5 figures et un broker à 6 figures par mois — verbalisée et activable.",
    "strategy.card.available": "Disponible après connexion",

    // ===== LANDING — EXERCISES =====
    "exercises.eyebrow": "Exercices pratiques",
    "exercises.title1": "Mises en situation",
    "exercises.title2": "réelles",
    "exercises.intro1": "La théorie sans pratique ne signe aucun contrat. Chaque module est complété par des cas concrets tirés de deals réels. Vous travaillez sur des situations où vous devez qualifier un acheteur, monter une chaîne, sécuriser votre commission ou détecter un piège — exactement comme sur le terrain.",
    "exercises.intro2": "C'est là que la formation se transforme en savoir-faire : vous ne devinez plus, vous appliquez.",
    "exercises.card1.title": "Deals internationaux",
    "exercises.card1.body": "Cas pratiques multi-secteurs : qualification d'acheteur, montage de chaîne, sécurisation IMFPA, détection des pièges classiques. À traiter étape par étape.",
    "exercises.card2.title": "Or — Application terrain",
    "exercises.card2.body": "Sourcing au Mali, vérification d'un Doré Bar, négociation de prime LBMA / Dubai, séquence LOI → SPA → SBLC. Application directe du module Or.",
    "exercises.card_label": "Exercice",
    "exercises.coming_soon_label": "À venir :",
    "exercises.coming_soon": "de nouveaux exercices sont ajoutés au fil des sessions — pétrole, pierres précieuses, soft commodities. Disponibles immédiatement pour tous les apprenants.",

    // ===== LANDING — RESOURCES =====
    "resources.eyebrow": "Ressources incluses",
    "resources.title1": "Documents officiels.",
    "resources.title2": "Contacts",
    "resources.title3": "vérifiés",
    "resources.intro": "Une bibliothèque opérationnelle — modèles finalisés et exemples réels téléchargeables — et un carnet d'adresses de plus de 40 fournisseurs vérifiés à travers le monde, avec WhatsApp, sites et téléphones cliquables. Gain de temps massif dès le premier deal.",
    "resources.kpi.suppliers.label": "Fournisseurs vérifiés",
    "resources.kpi.suppliers.sub": "Or · Pierres · Pétrole · Ciment · Commodités",
    "resources.kpi.docs.label": "Documents",
    "resources.kpi.docs.sub": "Tous officiels — pas de placeholders",
    "resources.row.required": "PDF · Connexion requise",
    "resources.row.nda": "Accord de confidentialité bilatéral",
    "resources.row.ncnda": "Non-Circumvention — protège l'intermédiaire",
    "resources.row.loi": "Lettre d'intention d'achat (exemple Thanatos)",
    "resources.row.spa": "Sales & Purchase Agreement (Marena Gold SBLC DXB)",
    "resources.row.sco": "Soft Corporate Offer — exemple pétrole",
    "resources.row.fco": "Full Corporate Offer — Millpond Petroleum (EN590)",
    "resources.row.spec": "Fiche technique — densité, flash point, cétane",
    "resources.row.cp": "Présentation société vendeur — Millpond",
    "resources.row.gia": "Diamond Grading Report — exemple réel",

    // ===== LANDING — WHY =====
    "why.eyebrow": "Pourquoi AURUM",
    "why.title1": "Une formation",
    "why.title2": "complète",
    "why.title3": "indispensable pour devenir apporteur d'affaires.",
    "why.intro": "La plupart des formations vous apprennent à parler du métier. AURUM vous apprend à signer. Vous repartez avec : la méthode complète sur 7 secteurs, 11 modules transversaux, des exercices terrain, plus de 40 fournisseurs vérifiés, tous les modèles documentaires officiels — et la posture d'un broker pris au sérieux.",
    "why.arg1.title": "Méthode vérifiée",
    "why.arg1.body": "Construite sur des deals réels et des centaines de credit records — pas sur de la théorie internet.",
    "why.arg2.title": "Crédibilité 100 %",
    "why.arg2.body": "Documents authentiques (LOI Thanatos, SPA Marena Gold, FCO Millpond) — vous voyez ce qui se signe vraiment.",
    "why.arg3.title": "Confidentielle",
    "why.arg3.body": "Accès nominatif par code unique. Contenu non redistribuable. Votre savoir reste votre avantage.",
    "why.arg4.title": "Orientée revenu",
    "why.arg4.body": "Chaque module ramène à une question : combien et comment encaisser à la fin ? Pas de remplissage.",

    // ===== LANDING — FINAL CTA =====
    "cta.eyebrow": "Accès privé",
    "cta.title1": "Prêt à signer",
    "cta.title2": "votre",
    "cta.title3": "premier contrat",
    "cta.body": "Chaque apprenant reçoit un code nominatif unique. Entrez-le pour déverrouiller l'intégralité du programme — 7 secteurs, 11 modules, exercices pratiques, documents officiels et carnet d'adresses de fournisseurs.",
    "cta.button": "Saisir mon code d'accès",

    // ===== PRICING / PAYPAL MODAL =====
    "pricing.eyebrow": "Tarif unique",
    "pricing.title1": "Un investissement",
    "pricing.title2": "unique",
    "pricing.title3": "pour un revenu récurrent",
    "pricing.intro": "Un seul paiement. Un accès à vie. Tous les modules, tous les secteurs, tous les documents officiels et l'intégralité du carnet d'adresses fournisseurs — disponibles immédiatement après le règlement.",
    "pricing.plan.name": "AURUM Formation Complète",
    "pricing.plan.price": "980",
    "pricing.plan.currency": "€",
    "pricing.plan.tag": "Paiement unique · Accès à vie",
    "pricing.feature.1": "7 secteurs · 11 modules · exercices pratiques",
    "pricing.feature.2": "40+ contacts fournisseurs vérifiés",
    "pricing.feature.3": "Documents officiels (LOI, NCNDA, SPA, FCO...)",
    "pricing.feature.4": "Mises à jour à vie, sans frais récurrents",
    "pricing.feature.5": "Code d'accès nominatif envoyé sous 24 h",
    "pricing.cta": "Acheter ma formation",
    "pricing.note": "Paiement sécurisé via PayPal · Carte bancaire acceptée",
    "modal.title": "Finaliser mon accès",
    "modal.summary.label": "Récapitulatif",
    "modal.summary.item": "AURUM Formation Complète",
    "modal.summary.total": "Total",
    "modal.guarantee": "Après paiement, votre code d'accès nominatif vous sera transmis manuellement par l'équipe AURUM sous 24 h ouvrées à l'adresse e-mail associée à votre PayPal.",
    "modal.has_code": "J'ai déjà un code d'accès",
    "modal.close": "Fermer",
    "modal.loading_paypal": "Chargement du paiement sécurisé…",

    // ===== LOGIN =====
    "login.title1": "Entrez votre",
    "login.title2": "code d'accès",
    "login.subtitle": "Chaque code est nominatif et déverrouille l'intégralité de la formation. Si vous n'avez pas encore reçu le vôtre, contactez l'équipe AURUM.",
    "login.label.code": "Code d'accès",
    "login.placeholder": "AURUM-XXXX-XXXX-XXXX",
    "login.submit": "Déverrouiller la formation",
    "login.submit.loading": "Vérification…",
    "login.footer.note": "Un code = un apprenant · Non transférable",
    "login.error.empty": "Veuillez saisir votre code d'accès.",
    "login.error.invalid": "Code d'accès invalide.",
    "login.welcome": "Bienvenue",
    "login.access_label": "Accès privé",

    // ===== DASHBOARD — SIDEBAR =====
    "dash.title": "Tableau de bord · AURUM Academy",
    "dash.group.training": "Formation",
    "dash.group.sectors": "Secteurs",
    "dash.group.strategy": "Stratégie",
    "dash.group.resources": "Ressources",
    "dash.learner": "Apprenant",
    "dash.session_active": "Accès privé · session active",
    "dash.admin_panel": "Panneau admin",
    "dash.logout": "Déconnexion",
    "dash.logout_toast": "Déconnecté. À très vite.",
    "dash.error_load": "Impossible de charger le contenu.",

    // Tab labels
    "tab.modules": "Modules",
    "tab.negotiation": "Négociation",
    "tab.banking": "Banking",
    "tab.security": "Sécurisation & Red Flags",
    "tab.pricing": "Pricing & Valorisation",
    "tab.taxation": "Fiscalité & Rémunération",
    "tab.toolkit": "Outils pratiques",
    "tab.gold": "Or",
    "tab.oil": "Pétrole",
    "tab.gems": "Pierres précieuses",
    "tab.watches": "Horlogerie",
    "tab.cement": "Ciment",
    "tab.metals": "Métaux",
    "tab.soft": "Commodités & Alimentaire",
    "tab.mindset": "Mindset du Deal Maker",
    "tab.deal-intel": "Deal Intelligence",
    "tab.power": "Pouvoir & Positionnement",
    "tab.network": "Accès & Réseau",
    "tab.documents": "Documents",
    "tab.exercices": "Exercices pratiques",
    "tab.suppliers": "Fournisseurs",

    // ===== DASHBOARD — VIEW : MODULES =====
    "modulesview.eyebrow": "Modules de formation",
    "modulesview.title": "Le programme complet, à votre rythme.",
    "modulesview.notes": "Notes de lecture",

    // ===== DASHBOARD — VIEW : DOCUMENTS =====
    "docview.eyebrow": "Documents officiels",
    "docview.title": "Votre bibliothèque opérationnelle.",
    "docview.subtitle": "Modèles finalisés et exemples réels, prêts à l'emploi — personnalisez-les avant signature.",
    "docview.tag": "PDF · Document",

    // ===== DASHBOARD — VIEW : EXERCICES =====
    "exview.eyebrow": "Mises en situation réelles",
    "exview.title1": "Exercices",
    "exview.title2": "pratiques",
    "exview.intro": "Cas pratiques tirés de deals réels — qualifier un acheteur, monter une chaîne, sécuriser sa commission, détecter les pièges. À travailler étape par étape, idéalement après avoir parcouru les modules de formation et le secteur correspondant.",
    "exview.empty": "Aucun exercice disponible pour le moment.",
    "exview.label": "Exercice",
    "exview.download": "Télécharger l'exercice",

    // ===== DASHBOARD — VIEW : SUPPLIERS =====
    "supview.eyebrow": "Contacts fournisseurs",
    "supview.title1": "Un carnet d'adresses",
    "supview.title2": "vérifié",
    "supview.intro": "Les acteurs de référence par secteur. Contactez-les directement avec une NDA signée et une démarche professionnelle.",
    "supview.count_label": "fournisseurs",
    "supview.no_website": "Site non communiqué",

    // Supplier categories (for the section headers)
    "supcat.Or": "Or",
    "supcat.Pierres précieuses": "Pierres précieuses",
    "supcat.Pétrole": "Pétrole",
    "supcat.Ciment": "Ciment",
    "supcat.Perles de culture": "Perles de culture",
    "supcat.Émeraudes, Rubis, Saphirs": "Émeraudes, Rubis, Saphirs",
    "supcat.Diamants": "Diamants",
    "supcat.Pétrole & Énergie": "Pétrole & Énergie",
    "supcat.Ciment & Commodités": "Ciment & Commodités",

    // ===== MODULES (Dashboard tab "Modules") =====
    "module.mod-intro.category": "Fondamentaux",
    "module.mod-intro.title": "Introduction à l'intermédiation commerciale",
    "module.mod-intro.description": "Comprendre le rôle d'intermédiaire, la chaîne de valeur et les obligations légales.",
    "module.mod-intro.reading": "L'intermédiation consiste à mettre en relation un acheteur et un vendeur moyennant une commission. Dans les matières premières à forte valeur (or, pierres précieuses, pétrole, ciment), la rigueur documentaire et la confidentialité sont essentielles. Vous apprendrez à qualifier un acheteur (POF), un vendeur (POP), à cadrer les négociations et à sécuriser votre commission.",
    "module.mod-intro.duration": "Lecture ~8 min",

    "module.mod-or.category": "Or",
    "module.mod-or.title": "Intermédiation sur l'or (Dore, lingots, bijoux)",
    "module.mod-or.description": "Sources légales, certifications LBMA, due diligence OCDE et circuits d'exportation.",
    "module.mod-or.reading": "L'or brut (Dore) nécessite une traçabilité stricte (Guide OCDE). Nous étudions les procédures Kimberley adjacentes, la certification LBMA, les incoterms (CIF / FOB) et la logistique sécurisée via raffineries agréées (Suisse, Dubaï, Singapour). Pour le détail complet, consultez la page dédiée 'Or' dans la sidebar.",
    "module.mod-or.duration": "Lecture ~10 min",

    "module.mod-pierres.category": "Pierres précieuses",
    "module.mod-pierres.title": "Diamants et pierres de couleur",
    "module.mod-pierres.description": "Processus de Kimberley, certifications GIA/HRD, négociation de lots.",
    "module.mod-pierres.reading": "Focus sur le diamant brut (Kimberley Process), le diamant taillé (GIA, HRD, IGI) et les pierres de couleur (rubis, saphir, émeraude). Vous apprendrez la lecture d'un certificat, les 4C, et les pratiques commerciales entre Anvers, Tel Aviv, Mumbai et Dubaï. Page dédiée : 'Pierres précieuses'.",
    "module.mod-pierres.duration": "Lecture ~10 min",

    "module.mod-horlogerie.category": "Horlogerie",
    "module.mod-horlogerie.title": "Marché secondaire des montres de luxe",
    "module.mod-horlogerie.description": "Sourcing, authentification, marges et plateformes de revente B2B.",
    "module.mod-horlogerie.reading": "Nous couvrons les marques piliers (Rolex, Patek Philippe, Audemars Piguet, Richard Mille, F.P. Journe), le sourcing auprès d'AD et de grossistes, l'authentification (papiers, box, mouvement) et les canaux de distribution B2B internationaux. Page dédiée : 'Horlogerie'.",
    "module.mod-horlogerie.duration": "Lecture ~9 min",

    "module.mod-petrole.category": "Pétrole",
    "module.mod-petrole.title": "Produits pétroliers : EN590, JP54, D2, Mazut",
    "module.mod-petrole.description": "Cadre TTO/TTT, procédures SBLC/DLC, documents SCO/FCO.",
    "module.mod-petrole.reading": "Le marché des produits pétroliers exige une maîtrise parfaite des procédures : SCO (Soft Corporate Offer), FCO (Full Corporate Offer), ICPO, SBLC, DLC, TSR, TSA. Nous décortiquons un deal CIF Rotterdam et un deal FOB Houston étape par étape. Page dédiée : 'Pétrole'.",
    "module.mod-petrole.duration": "Lecture ~12 min",

    "module.mod-ciment.category": "Ciment",
    "module.mod-ciment.title": "Ciment, clinker et matériaux de construction",
    "module.mod-ciment.description": "Spécifications techniques, grands fournisseurs et logistique conteneurs/vrac.",
    "module.mod-ciment.reading": "Les grades (CEM I 42,5 / 52,5), le clinker, les big bags et le vrac. Nous étudions les flux Turquie-Afrique, Vietnam-Afrique, les acteurs majeurs (Lafarge, Holcim, Dangote) et la structuration d'un contrat-cadre. Page dédiée : 'Ciment'.",
    "module.mod-ciment.duration": "Lecture ~9 min",

    "module.mod-docs.category": "Procédures",
    "module.mod-docs.title": "Maîtriser les documents clés : NDA, NCNDA, LOI, ICPO",
    "module.mod-docs.description": "Rédaction, pièges fréquents et protection de votre commission.",
    "module.mod-docs.reading": "NDA (confidentialité), NCNDA (non-circonvention), LOI (lettre d'intention), ICPO (commande irrévocable). Protection via IMFPA. Ne signez jamais sans ces garde-fous. Téléchargez les modèles dans l'onglet 'Documents'.",
    "module.mod-docs.duration": "Lecture ~7 min",
  },

  en: {
    // ===== COMMON =====
    "common.loading": "Loading…",
    "common.loading_dashboard": "Loading the program…",
    "common.access": "Sign in",
    "common.access_full": "Enter the program",
    "common.see_program": "See the curriculum",
    "common.discover": "Discover",
    "common.download": "Download",
    "common.download_pdf": "Download PDF",
    "common.contact": "Contact",
    "common.back_home": "Back to home",
    "common.year_copyright": "© 2026 — All rights reserved",

    // ===== LANG TOGGLE =====
    "lang.fr": "FR",
    "lang.en": "EN",
    "lang.toggle_aria": "Switch language",

    // ===== LANDING — HEADER =====
    "header.nav.promise": "Promise",
    "header.nav.sectors": "Sectors",
    "header.nav.training": "Training",
    "header.nav.strategy": "Strategy",
    "header.nav.resources": "Resources",

    // ===== LANDING — HERO =====
    "hero.eyebrow": "Become an international deal maker",
    "hero.title.from": "Go from",
    "hero.title.to": "to",
    "hero.title.permonth": "per month",
    "hero.title.signing": "by signing",
    "hero.title.contracts": "contracts",
    "hero.subtitle": "The step-by-step method to source, qualify, structure and close high-value transactions in gold, gemstones, oil, metals, watches, cement and soft commodities — and actually collect your commission without getting bypassed.",
    "hero.badge.method": "Field-tested method",
    "hero.badge.records": "Hundreds of credit records",
    "hero.badge.deals": "Documented successful deals",
    "hero.stat.sectors": "Sectors covered",
    "hero.stat.modules": "Modules",
    "hero.stat.suppliers": "Suppliers",
    "hero.note": "Named access by unique code. Non-redistributable content. Your role as an intermediary is protected by",

    // ===== LANDING — MARQUEE =====
    "marquee.gold": "Raw Gold & Doré",
    "marquee.diamonds": "GIA Diamonds",
    "marquee.watches": "B2B Watches",
    "marquee.oil": "EN590 · JP54 · Mazut",
    "marquee.metals": "Copper · Aluminum · Nickel",
    "marquee.soft": "Sugar · Coffee · Cocoa",
    "marquee.docs": "NCNDA · LOI · ICPO · IMFPA",

    // ===== LANDING — PROMISE =====
    "promise.eyebrow": "The promise",
    "promise.title1": "A",
    "promise.title2": "proven",
    "promise.title3": "method. Credibility at",
    "promise.intro": "No fluff theory. This program is built on hundreds of credit records, real signed deals, and the concrete mistakes 99% of intermediaries make. You inherit the system — not the war stories.",
    "promise.pillar1.title": "Find the right deals",
    "promise.pillar1.body": "Source solvent buyers and sellers who actually deliver. Decode an SCO, ICPO or FCO in 2 minutes. Filter out 95% of amateurs before you even reply.",
    "promise.pillar2.title": "Structure & secure",
    "promise.pillar2.body": "NCNDA, LOI, IMFPA, SBLC, MT760 — the documentary sequence that locks in your commission. No more bypass, no more ghost chains.",
    "promise.pillar3.title": "Get paid quietly",
    "promise.pillar3.body": "Banking, jurisdiction, fee structure. Take in $5k, $10k, $20k a month cleanly, with optimised tax positioning from day one.",
    "promise.kpi1.label": "Monthly target",
    "promise.kpi2.label": "Gold, oil, metals, soft…",
    "promise.kpi2.value": "7 sectors",
    "promise.kpi3.label": "From sourcing to taxation",
    "promise.kpi3.value": "11 modules",
    "promise.kpi4.label": "Verified suppliers",
    "promise.kpi4.value": "40+ contacts",

    // ===== LANDING — SECTORS =====
    "sectors.eyebrow": "Sectors covered",
    "sectors.title1": "Seven markets.",
    "sectors.title2": "One single method.",
    "sectors.intro": "Each sector has its own documentary liturgy, its reference exchanges, its pitfalls and its regional premiums. AURUM unpacks all of them — from sourcing to signature. Start with the sector you want, and unlock the others when you choose.",
    "sectors.gold.title": "Gold",
    "sectors.gold.sub": "Doré, bullion, LBMA / Dubai refining",
    "sectors.oil.title": "Oil",
    "sectors.oil.sub": "EN590 · JP54 · Mazut · D2 · Crude",
    "sectors.gems.title": "Gemstones",
    "sectors.gems.sub": "GIA diamonds · emeralds · rubies · sapphires",
    "sectors.watches.title": "Watches",
    "sectors.watches.sub": "Rolex · AP · Patek · Richard Mille — B2B",
    "sectors.cement.title": "Cement",
    "sectors.cement.sub": "Clinker · CEM I-V · bulk & big bag",
    "sectors.metals.title": "Metals",
    "sectors.metals.sub": "Cu · Al · Zn · Pb · Ni · Sn · Ag · Co · Mg",
    "sectors.soft.title": "Soft & Food Commodities",
    "sectors.soft.sub": "Sugar · Coffee · Cocoa · Wheat · Rice · Palm",

    // ===== LANDING — FORMATION =====
    "formation.eyebrow": "Training modules",
    "formation.title1": "The core craft.",
    "formation.title2": "Seven",
    "formation.title3": "essential",
    "formation.intro": "From negotiation to taxation, including deal security and international banking — you get the complete system to turn an opportunity into commission actually banked.",
    "formation.m1.title": "International negotiation",
    "formation.m1.body": "Harvard method applied to brokering. The 5 steps of a winning negotiation, sector-by-sector commissions, handling silence, spotting bluffs, navigating the 4 cultural zones and reading the 7 exit signals.",
    "formation.m2.title": "Banking",
    "formation.m2.body": "8 strategic jurisdictions with their tax rates, how to open an account in the UAE and Mauritius, the SWIFT network and correspondent banks, wire fees compared, offshore vs foreign.",
    "formation.m3.title": "Security & Red Flags",
    "formation.m3.body": "The 5 classic brokerage scams, how to verify an LC or SBLC in 6 steps, the 3T rule, KYC in 5 steps with the 8 tools to use.",
    "formation.m4.title": "Pricing & Valuation",
    "formation.m4.body": "Official price sources by commodity, reading a Bloomberg quote, understanding premiums and discounts, calculating the price of 1 metric ton of gold, FOB vs CIF.",
    "formation.m5.title": "Tax & Compensation",
    "formation.m5.body": "The 3 fee structures in brokerage, 6 entity types (SASU, UAE LLC, DMCC, Mauritius GBL…), bilateral treaties, choosing based on your profile and volume.",
    "formation.m6.title": "Practical toolkit",
    "formation.m6.body": "4 email templates with copy buttons, 4 recommended CRMs, 5 e-signature platforms, the 10 golden rules of the broker, 20 clickable bookmarks by category.",
    "formation.m7.title": "Deal Maker mindset",
    "formation.m7.body": "Posture, identity, managing the long sales cycle. How to think like a respected broker rather than a passing middleman. The inner work nobody teaches.",

    // ===== LANDING — STRATEGY =====
    "strategy.eyebrow": "Strategic modules",
    "strategy.title1": "Technique alone is not enough.",
    "strategy.title2": "",
    "strategy.title3": "Positioning",
    "strategy.title4": "decides.",
    "strategy.intro": "Four cross-cutting modules that separate a passing intermediary from a respected broker. This is what turns technical mastery into stable monthly revenue.",
    "strategy.s1.title": "Deal Intelligence & Filtering",
    "strategy.s1.body": "Spot a fake seller in 2 minutes, decode red flags in an SCO / ICPO / FCO, neutralise the 4 classic scam structures (daisy chain, fake mandate, advance fee, booby-trapped test shipment).",
    "strategy.s2.title": "Power & Positioning",
    "strategy.s2.body": "Become a mandate rather than a middleman. Make yourself indispensable in a chain. Lock in your commission via NCNDA and IMFPA. Speak the language of HNWIs, traders and suppliers.",
    "strategy.s3.title": "Access & Network",
    "strategy.s3.body": "Approach a supplier without being ignored. Ready-to-use scripts for Email / LinkedIn / WhatsApp. Build a credible network of 20 useful contacts in 30 days, week by week.",
    "strategy.s4.title": "Cross-cutting mindset",
    "strategy.s4.body": "The deal maker identity: posture, long-cycle management, resilience. The mental difference between a 5-figure and a 6-figure broker — verbalised and actionable.",
    "strategy.card.available": "Available after sign-in",

    // ===== LANDING — EXERCISES =====
    "exercises.eyebrow": "Practical exercises",
    "exercises.title1": "Real-life",
    "exercises.title2": "case work",
    "exercises.intro1": "Theory without practice closes no deal. Each module is paired with concrete cases drawn from real transactions. You work on situations where you must qualify a buyer, build a chain, secure your commission or spot a trap — exactly like in the field.",
    "exercises.intro2": "This is where the program becomes know-how: you stop guessing, you apply.",
    "exercises.card1.title": "International deals",
    "exercises.card1.body": "Multi-sector cases: buyer qualification, chain building, IMFPA security, classic-trap detection. Work through them step by step.",
    "exercises.card2.title": "Gold — Field application",
    "exercises.card2.body": "Sourcing in Mali, verifying a Doré bar, negotiating LBMA / Dubai premium, the LOI → SPA → SBLC sequence. Direct application of the Gold module.",
    "exercises.card_label": "Exercise",
    "exercises.coming_soon_label": "Coming up:",
    "exercises.coming_soon": "new exercises will be added with each cohort — oil, gemstones, soft commodities. Available immediately to all learners.",

    // ===== LANDING — RESOURCES =====
    "resources.eyebrow": "Included resources",
    "resources.title1": "Official documents.",
    "resources.title2": "",
    "resources.title3": "Verified contacts",
    "resources.intro": "An operational library — finalised templates and real downloadable examples — and an address book of 40+ verified suppliers across the world, with clickable WhatsApp, sites and phones. Massive time-saver from your first deal.",
    "resources.kpi.suppliers.label": "Verified suppliers",
    "resources.kpi.suppliers.sub": "Gold · Gemstones · Oil · Cement · Commodities",
    "resources.kpi.docs.label": "Documents",
    "resources.kpi.docs.sub": "All official — no placeholders",
    "resources.row.required": "PDF · Sign-in required",
    "resources.row.nda": "Bilateral non-disclosure agreement",
    "resources.row.ncnda": "Non-Circumvention — protects the intermediary",
    "resources.row.loi": "Letter of Intent (Thanatos example)",
    "resources.row.spa": "Sales & Purchase Agreement (Marena Gold SBLC DXB)",
    "resources.row.sco": "Soft Corporate Offer — oil example",
    "resources.row.fco": "Full Corporate Offer — Millpond Petroleum (EN590)",
    "resources.row.spec": "Spec sheet — density, flash point, cetane",
    "resources.row.cp": "Seller company profile — Millpond",
    "resources.row.gia": "Diamond Grading Report — real example",

    // ===== LANDING — WHY =====
    "why.eyebrow": "Why AURUM",
    "why.title1": "A",
    "why.title2": "complete",
    "why.title3": "program, indispensable to become a real deal maker.",
    "why.intro": "Most courses teach you to talk about the trade. AURUM teaches you to sign. You leave with: the complete method across 7 sectors, 11 transversal modules, field exercises, 40+ verified suppliers, every official documentary template — and the posture of a broker taken seriously.",
    "why.arg1.title": "Proven method",
    "why.arg1.body": "Built on real deals and hundreds of credit records — not on internet theory.",
    "why.arg2.title": "100% credibility",
    "why.arg2.body": "Authentic documents (Thanatos LOI, Marena Gold SPA, Millpond FCO) — you see what actually gets signed.",
    "why.arg3.title": "Confidential",
    "why.arg3.body": "Named access by unique code. Non-redistributable content. Your knowledge stays your edge.",
    "why.arg4.title": "Revenue-driven",
    "why.arg4.body": "Every module loops back to one question: how much, and how to get paid? No filler.",

    // ===== LANDING — FINAL CTA =====
    "cta.eyebrow": "Private access",
    "cta.title1": "Ready to sign",
    "cta.title2": "your",
    "cta.title3": "first contract",
    "cta.body": "Each learner receives a unique named code. Enter it to unlock the full program — 7 sectors, 11 modules, practical exercises, official documents and a supplier address book.",
    "cta.button": "Enter my access code",

    // ===== PRICING / PAYPAL MODAL =====
    "pricing.eyebrow": "Single price",
    "pricing.title1": "A",
    "pricing.title2": "one-time",
    "pricing.title3": "investment for recurring income",
    "pricing.intro": "One payment. Lifetime access. Every module, every sector, every official document and the entire supplier address book — available immediately after payment.",
    "pricing.plan.name": "AURUM Complete Program",
    "pricing.plan.price": "980",
    "pricing.plan.currency": "€",
    "pricing.plan.tag": "One-time payment · Lifetime access",
    "pricing.feature.1": "7 sectors · 11 modules · practical exercises",
    "pricing.feature.2": "40+ verified supplier contacts",
    "pricing.feature.3": "Official documents (LOI, NCNDA, SPA, FCO...)",
    "pricing.feature.4": "Lifetime updates, no recurring fees",
    "pricing.feature.5": "Named access code sent within 24 hours",
    "pricing.cta": "Buy the program",
    "pricing.note": "Secure payment via PayPal · Card accepted",
    "modal.title": "Complete my access",
    "modal.summary.label": "Order summary",
    "modal.summary.item": "AURUM Complete Program",
    "modal.summary.total": "Total",
    "modal.guarantee": "After payment, your named access code will be sent to you manually by the AURUM team within 24 business hours at the email address linked to your PayPal account.",
    "modal.has_code": "I already have an access code",
    "modal.close": "Close",
    "modal.loading_paypal": "Loading secure checkout…",

    // ===== LOGIN =====
    "login.title1": "Enter your",
    "login.title2": "access code",
    "login.subtitle": "Each code is named and unlocks the entire program. If you have not received yours yet, get in touch with the AURUM team.",
    "login.label.code": "Access code",
    "login.placeholder": "AURUM-XXXX-XXXX-XXXX",
    "login.submit": "Unlock the program",
    "login.submit.loading": "Verifying…",
    "login.footer.note": "One code = one learner · Non-transferable",
    "login.error.empty": "Please enter your access code.",
    "login.error.invalid": "Invalid access code.",
    "login.welcome": "Welcome",
    "login.access_label": "Private access",

    // ===== DASHBOARD — SIDEBAR =====
    "dash.title": "Dashboard · AURUM Academy",
    "dash.group.training": "Training",
    "dash.group.sectors": "Sectors",
    "dash.group.strategy": "Strategy",
    "dash.group.resources": "Resources",
    "dash.learner": "Learner",
    "dash.session_active": "Private access · live session",
    "dash.admin_panel": "Admin panel",
    "dash.logout": "Sign out",
    "dash.logout_toast": "Signed out. See you soon.",
    "dash.error_load": "Unable to load content.",

    // Tab labels
    "tab.modules": "Modules",
    "tab.negotiation": "Negotiation",
    "tab.banking": "Banking",
    "tab.security": "Security & Red Flags",
    "tab.pricing": "Pricing & Valuation",
    "tab.taxation": "Tax & Compensation",
    "tab.toolkit": "Practical Toolkit",
    "tab.gold": "Gold",
    "tab.oil": "Oil",
    "tab.gems": "Gemstones",
    "tab.watches": "Watches",
    "tab.cement": "Cement",
    "tab.metals": "Metals",
    "tab.soft": "Soft & Food",
    "tab.mindset": "Deal Maker Mindset",
    "tab.deal-intel": "Deal Intelligence",
    "tab.power": "Power & Positioning",
    "tab.network": "Access & Network",
    "tab.documents": "Documents",
    "tab.exercices": "Practical Exercises",
    "tab.suppliers": "Suppliers",

    // ===== DASHBOARD — VIEW : MODULES =====
    "modulesview.eyebrow": "Training modules",
    "modulesview.title": "The complete curriculum, at your own pace.",
    "modulesview.notes": "Reading notes",

    // ===== DASHBOARD — VIEW : DOCUMENTS =====
    "docview.eyebrow": "Official documents",
    "docview.title": "Your operational library.",
    "docview.subtitle": "Finalised templates and real examples, ready to use — customise them before signing.",
    "docview.tag": "PDF · Document",

    // ===== DASHBOARD — VIEW : EXERCISES =====
    "exview.eyebrow": "Real case work",
    "exview.title1": "Practical",
    "exview.title2": "exercises",
    "exview.intro": "Cases drawn from real deals — qualify a buyer, build a chain, secure your commission, spot the traps. Work through them step by step, ideally after going through the training modules and the corresponding sector.",
    "exview.empty": "No exercise available yet.",
    "exview.label": "Exercise",
    "exview.download": "Download exercise",

    // ===== DASHBOARD — VIEW : SUPPLIERS =====
    "supview.eyebrow": "Supplier contacts",
    "supview.title1": "A",
    "supview.title2": "verified",
    "supview.title3": "address book",
    "supview.intro": "Reference players, by sector. Reach out directly with a signed NDA and a professional approach.",
    "supview.count_label": "suppliers",
    "supview.no_website": "Website not provided",

    // Supplier categories
    "supcat.Or": "Gold",
    "supcat.Pierres précieuses": "Gemstones",
    "supcat.Pétrole": "Oil",
    "supcat.Ciment": "Cement",
    "supcat.Perles de culture": "Cultured Pearls",
    "supcat.Émeraudes, Rubis, Saphirs": "Emeralds, Rubies, Sapphires",
    "supcat.Diamants": "Diamonds",
    "supcat.Pétrole & Énergie": "Oil & Energy",
    "supcat.Ciment & Commodités": "Cement & Commodities",

    // ===== MODULES (Dashboard tab "Modules") =====
    "module.mod-intro.category": "Fundamentals",
    "module.mod-intro.title": "Introduction to commercial brokerage",
    "module.mod-intro.description": "Understand the broker's role, the value chain and the legal obligations.",
    "module.mod-intro.reading": "Brokerage consists of connecting a buyer and a seller in exchange for a commission. In high-value commodities (gold, gemstones, oil, cement), documentary rigor and confidentiality are essential. You will learn to qualify a buyer (POF), a seller (POP), to frame negotiations and to secure your commission.",
    "module.mod-intro.duration": "Reading ~8 min",

    "module.mod-or.category": "Gold",
    "module.mod-or.title": "Brokerage on gold (Doré, bullion, jewelry)",
    "module.mod-or.description": "Legal sources, LBMA certifications, OECD due diligence and export circuits.",
    "module.mod-or.reading": "Raw gold (Doré) requires strict traceability (OECD Guide). We study the adjacent Kimberley procedures, LBMA certification, incoterms (CIF / FOB) and secure logistics via accredited refineries (Switzerland, Dubai, Singapore). For full detail, see the dedicated 'Gold' page in the sidebar.",
    "module.mod-or.duration": "Reading ~10 min",

    "module.mod-pierres.category": "Gemstones",
    "module.mod-pierres.title": "Diamonds and colored stones",
    "module.mod-pierres.description": "Kimberley Process, GIA/HRD certifications, lot negotiation.",
    "module.mod-pierres.reading": "Focus on rough diamonds (Kimberley Process), polished diamonds (GIA, HRD, IGI) and colored stones (ruby, sapphire, emerald). You will learn how to read a certificate, the 4Cs, and the trading practices between Antwerp, Tel Aviv, Mumbai and Dubai. Dedicated page: 'Gemstones'.",
    "module.mod-pierres.duration": "Reading ~10 min",

    "module.mod-horlogerie.category": "Watches",
    "module.mod-horlogerie.title": "Secondary market for luxury watches",
    "module.mod-horlogerie.description": "Sourcing, authentication, margins and B2B resale platforms.",
    "module.mod-horlogerie.reading": "We cover the pillar brands (Rolex, Patek Philippe, Audemars Piguet, Richard Mille, F.P. Journe), sourcing from authorized dealers and wholesalers, authentication (papers, box, movement) and international B2B distribution channels. Dedicated page: 'Watches'.",
    "module.mod-horlogerie.duration": "Reading ~9 min",

    "module.mod-petrole.category": "Oil",
    "module.mod-petrole.title": "Oil products: EN590, JP54, D2, Mazut",
    "module.mod-petrole.description": "TTO/TTT framework, SBLC/DLC procedures, SCO/FCO documents.",
    "module.mod-petrole.reading": "The oil products market requires perfect mastery of the procedures: SCO (Soft Corporate Offer), FCO (Full Corporate Offer), ICPO, SBLC, DLC, TSR, TSA. We dissect a CIF Rotterdam deal and a FOB Houston deal step by step. Dedicated page: 'Oil'.",
    "module.mod-petrole.duration": "Reading ~12 min",

    "module.mod-ciment.category": "Cement",
    "module.mod-ciment.title": "Cement, clinker and construction materials",
    "module.mod-ciment.description": "Technical specifications, major suppliers and container/bulk logistics.",
    "module.mod-ciment.reading": "Grades (CEM I 42.5 / 52.5), clinker, big bags and bulk. We study Turkey-Africa, Vietnam-Africa flows, the major players (Lafarge, Holcim, Dangote) and the structuring of a framework contract. Dedicated page: 'Cement'.",
    "module.mod-ciment.duration": "Reading ~9 min",

    "module.mod-docs.category": "Procedures",
    "module.mod-docs.title": "Mastering the key documents: NDA, NCNDA, LOI, ICPO",
    "module.mod-docs.description": "Drafting, common pitfalls and protecting your commission.",
    "module.mod-docs.reading": "NDA (confidentiality), NCNDA (non-circumvention), LOI (letter of intent), ICPO (irrevocable purchase order). Protection via IMFPA. Never sign without these safeguards. Download the templates in the 'Documents' tab.",
    "module.mod-docs.duration": "Reading ~7 min",
  },
};

const LangContext = createContext({ lang: "fr", setLang: () => {}, t: (k) => k });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "fr";
    const stored = window.localStorage.getItem(KEY);
    if (stored === "en" || stored === "fr") return stored;
    // Auto-detect browser language on first visit
    const nav = (window.navigator?.language || window.navigator?.userLanguage || "fr").toLowerCase();
    return nav.startsWith("en") ? "en" : "fr";
  });

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next);
    if (typeof window !== "undefined") window.localStorage.setItem(KEY, next);
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const table = dict[lang] || dict.fr;
      if (key in table) return table[key];
      if (fallback != null) return fallback;
      // graceful fallback to FR
      return dict.fr[key] != null ? dict.fr[key] : key;
    },
    [lang]
  );

  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
};

export const useT = () => useContext(LangContext);
